// Property Yardi Configuration Section - Yardi Voyager credentials
import React from 'react';
import { Text, StyleSheet } from 'react-native';
import { FormSection, FormField } from '@/components/common/admin/form';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface PropertyYardiSectionProps {
  yardiDomain: string;
  yardiUsername: string;
  yardiPassword: string;
  yardiServerName: string;
  yardiDatabase: string;
  yardiBpInterfaceEntity: string;
  yardiBpInterfaceLicense: string;
  onYardiDomainChange: (value: string) => void;
  onYardiUsernameChange: (value: string) => void;
  onYardiPasswordChange: (value: string) => void;
  onYardiServerNameChange: (value: string) => void;
  onYardiDatabaseChange: (value: string) => void;
  onYardiBpInterfaceEntityChange: (value: string) => void;
  onYardiBpInterfaceLicenseChange: (value: string) => void;
  disabled?: boolean;
}

export const PropertyYardiSection: React.FC<PropertyYardiSectionProps> = ({
  yardiDomain,
  yardiUsername,
  yardiPassword,
  yardiServerName,
  yardiDatabase,
  yardiBpInterfaceEntity,
  yardiBpInterfaceLicense,
  onYardiDomainChange,
  onYardiUsernameChange,
  onYardiPasswordChange,
  onYardiServerNameChange,
  onYardiDatabaseChange,
  onYardiBpInterfaceEntityChange,
  onYardiBpInterfaceLicenseChange,
  disabled,
}) => {
  const styles = useStyles();
  return (
    <FormSection title="Yardi Configuration">
      <Text style={styles.helpText}>
        Enter Yardi Voyager API credentials for this property. These credentials will be used to sync resident balances and process payments.
      </Text>

      <FormField
        label="Yardi Domain"
        placeholder="client.yardi.com"
        value={yardiDomain}
        onChangeText={onYardiDomainChange}
        editable={!disabled}
        autoCapitalize="none"
        autoCorrect={false}
        required
      />

      <FormField
        label="Username"
        placeholder="Yardi API username"
        value={yardiUsername}
        onChangeText={onYardiUsernameChange}
        editable={!disabled}
        autoCapitalize="none"
        autoCorrect={false}
        required
      />

      <FormField
        label="Password"
        placeholder="Yardi API password"
        value={yardiPassword}
        onChangeText={onYardiPasswordChange}
        editable={!disabled}
        secureTextEntry
        autoCapitalize="none"
        autoCorrect={false}
        required
      />

      <FormField
        label="Server Name"
        placeholder="SQL Server name"
        value={yardiServerName}
        onChangeText={onYardiServerNameChange}
        editable={!disabled}
        autoCapitalize="none"
        autoCorrect={false}
        required
      />

      <FormField
        label="Database"
        placeholder="Voyager database name"
        value={yardiDatabase}
        onChangeText={onYardiDatabaseChange}
        editable={!disabled}
        autoCapitalize="none"
        autoCorrect={false}
        required
      />

      <FormField
        label="BP Interface Entity"
        placeholder="Resio"
        value={yardiBpInterfaceEntity}
        onChangeText={onYardiBpInterfaceEntityChange}
        editable={!disabled}
        autoCorrect={false}
      />

      <FormField
        label="BP Interface License"
        placeholder="License key from Yardi"
        value={yardiBpInterfaceLicense}
        onChangeText={onYardiBpInterfaceLicenseChange}
        editable={!disabled}
        autoCapitalize="none"
        autoCorrect={false}
        required
      />

      <Text style={styles.infoText}>
        💡 Tip: Contact your Yardi account manager to obtain these credentials if you don't have them.
      </Text>
    </FormSection>
  );
};

const useStyles = () =>
  useThemedStyles(({ colors, components }) =>
    StyleSheet.create({
      helpText: {
        fontSize: 12,
        color: components.admin.formInput.label,
        marginBottom: 16,
        lineHeight: 18,
      },
      infoText: {
        fontSize: 11,
        color: colors.text.dim,
        marginTop: 8,
        fontStyle: 'italic',
      },
    }),
  );
